import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import { TranslationsGuard } from 'app/services/translations.guard';
import {
    AddPasswordlessAccesses,
    CreatePasswordlessAccess,
    CreatePasswordlessAccessFailure,
    CreatePasswordlessAccessSuccess,
    DeletePasswordlessAccess,
    DeletePasswordlessAccessFailure,
    DeletePasswordlessAccessSuccess,
    FetchPasswordlessAccess,
    FetchPasswordlessAccessFailure,
    FetchPasswordlessAccessSuccess,
    SendPasswordlessAccess,
    SendPasswordlessAccessFailure,
    SendPasswordlessAccessSuccess,
    SetPasswordlessAccess,
    SetPasswordlessAccesses,
    UnsetPasswordlessAccess,
    UpdatePasswordlessAccess,
    UpdatePasswordlessAccessFailure,
    UpdatePasswordlessAccessSuccess,
} from '../actions';
import { PasswordlessAccessApiService } from '../services/services/passwordless-access.api-service';
import { PasswordlessAccess } from './passwordless-access.model';
import { PasswordlessAccessStateModel } from './passwordless-access.state-model';

@State<PasswordlessAccessStateModel>({
    name: 'passwordlessAccess',
    defaults: new PasswordlessAccessStateModel(),
})
@Injectable()
export class PasswordlessAccessState extends EntityState<PasswordlessAccess> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: PasswordlessAccessStateModel, id: string = state.item): PasswordlessAccess {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: PasswordlessAccessStateModel): PasswordlessAccess[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(PasswordlessAccessState.sortProperties));

        return toReturn;
    }

    constructor(private _passwordlessAccessApiService: PasswordlessAccessApiService, private _translationsGuard: TranslationsGuard) {
        super(null, 30);
    }

    @Action(AddPasswordlessAccesses)
    addEntities(ctx: StateContext<PasswordlessAccessStateModel>, action: AddPasswordlessAccesses): void {
        this._addEntities(ctx, action);
    }

    @Action(SetPasswordlessAccess)
    setEntity(ctx: StateContext<PasswordlessAccessStateModel>, action: SetPasswordlessAccess): void {
        this._setEntity(ctx, action, AddPasswordlessAccesses);
    }

    @Action(UnsetPasswordlessAccess)
    unsetEntity(ctx: StateContext<PasswordlessAccessStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreatePasswordlessAccess)
    createEntity(ctx: StateContext<PasswordlessAccessStateModel>, action: CreatePasswordlessAccess): void {
        this._passwordlessAccessApiService.create(action.payload).subscribe(
            (entities) => {
                ctx.dispatch([new AddPasswordlessAccesses(entities), new CreatePasswordlessAccessSuccess(entities)]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new CreatePasswordlessAccessFailure({ response }));
            }
        );
    }

    @Action(UpdatePasswordlessAccess)
    updateEntity(ctx: StateContext<PasswordlessAccessStateModel>, action: UpdatePasswordlessAccess): void {
        this._passwordlessAccessApiService.update(action.payload).subscribe(
            (entities) => {
                ctx.dispatch([new AddPasswordlessAccesses(entities), new UpdatePasswordlessAccessSuccess(entities)]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdatePasswordlessAccessFailure());
            }
        );
    }

    @Action(DeletePasswordlessAccess)
    deleteEntity(ctx: StateContext<PasswordlessAccessStateModel>, action: DeletePasswordlessAccess): void {
        this._passwordlessAccessApiService.delete(action.payload).subscribe(
            (entities) => {
                ctx.dispatch(new DeletePasswordlessAccessSuccess(action.payload));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new DeletePasswordlessAccessFailure());
            }
        );
    }

    @Action(SendPasswordlessAccess)
    sendPasswordlessAccess(ctx: StateContext<PasswordlessAccessStateModel>, action: SendPasswordlessAccess): void {
        this._passwordlessAccessApiService.sendPasswordlessAccess(action.payload.accessIds, action.payload.subject, action.payload.message).subscribe(
            (entities) => {
                ctx.dispatch(new SendPasswordlessAccessSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new SendPasswordlessAccessFailure());
            }
        );
    }

    @Action(FetchPasswordlessAccess)
    fetchEntity(ctx: StateContext<PasswordlessAccessStateModel>, action: FetchPasswordlessAccess): void {
        this._fetchEntity(ctx, action, FetchPasswordlessAccessSuccess, FetchPasswordlessAccessFailure, AddPasswordlessAccesses, SetPasswordlessAccess).then(() => {});
    }

    @Action(FetchPasswordlessAccessSuccess)
    fetchEntitySuccess(ctx: StateContext<PasswordlessAccessStateModel>, action: FetchPasswordlessAccessSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchPasswordlessAccessFailure)
    fetchEntityFailure(ctx: StateContext<PasswordlessAccessStateModel>, action: FetchPasswordlessAccessFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetPasswordlessAccesses)
    setEntities(ctx: StateContext<PasswordlessAccessStateModel>, action: SetPasswordlessAccesses): void {
        this._setEntities(ctx, action);
    }
}
